import React from "react";
import type { BrandSummary, CampaignReportResponse } from "../../types";
import type { ExportTemplateModule } from "../types";
import { coerceNumber, formatCurrency, getTopBrands } from "../utils";

interface BrandShareModuleProps {
  data: CampaignReportResponse;
  module: ExportTemplateModule<"brand-share">;
}

export const BrandShareModule: React.FC<BrandShareModuleProps> = ({
  data,
  module,
}) => {
  const metric = module.props?.metric || "total_sales";
  const brands = getTopBrands((data.brands ?? []) as BrandSummary[], metric, 4);

  if (!brands.length) {
    return (
      <section>
        {module.title && (
          <div className="text-slate-400 uppercase tracking-[0.2em] text-xs font-semibold mb-3">
            {module.title}
          </div>
        )}
        <div className="rounded-2xl bg-white border border-slate-100 shadow-[0_20px_45px_rgba(15,23,42,0.08)] p-6 text-slate-500 text-sm">
          Brand-level data unavailable.
        </div>
      </section>
    );
  }

  const total = brands.reduce((sum, brand) => sum + coerceNumber(brand[metric]), 0);

  return (
    <section>
      {module.title && (
        <div className="text-slate-400 uppercase tracking-[0.2em] text-xs font-semibold mb-3">
          {module.title}
        </div>
      )}
      <div className="rounded-2xl bg-white border border-slate-100 shadow-[0_20px_45px_rgba(15,23,42,0.08)] p-6">
        <div className="flex flex-col gap-4">
          {brands.map((brand) => {
            const value = coerceNumber(brand[metric]);
            const share = total > 0 ? (value / total) * 100 : 0;
            return (
              <div key={brand.brand} className="space-y-2">
                <div className="flex items-center justify-between text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">
                    {brand.brand}
                  </span>
                  <span className="font-medium">
                    {formatCurrency(value, { maximumFractionDigits: 0 })} •{" "}
                    {share.toFixed(1)}%
                  </span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-emerald-300 to-sky-400"
                    style={{ width: `${share}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
